/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect } from 'react';

import { Locale, LocaleKeys } from 'src/core/locale';

import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { setLastFocusedType } from '../../../store/seasonpicker/seasonpicker.reducers';
import { Mousetrap } from '../index';

import './SeasonPicker.scss';

type SeasonPickerProps =
    | {
          id: string;
          type: 'header';
          title: string;
          listItemIndex?: number;
          mainListPosition?: number;
          isMainListFocused?: boolean;
          ghostMode?: boolean;
      }
    | {
          id: string;
          type: 'picker';
          title: string;
          items: Array<{
              listItemIndex: number;
              text: string;
          }>;
          listItemIndex?: number;
          mainListPosition?: number;
          isMainListFocused?: boolean;
          ghostMode?: boolean;
      };

const SeasonPicker: FunctionComponent<SeasonPickerProps> = (props: SeasonPickerProps) => {
    const dispatch = useTivoDispatch();

    const highlightedItemIndex =
        useTivoSelector((state: RootState) => state.seasonPicker.pickers[props.id]?.index) || 0;

    const isPickerFocused = useTivoSelector(
        (state: RootState) => state.seasonPicker.pickers[props.id]?.isFocused,
    );

    const lastFocusedType = useTivoSelector(
        (state: RootState) => state.seasonPicker.lastFocusedType ?? null,
    );

    useEffect(() => {
        if (isPickerFocused) {
            dispatch(setLastFocusedType('picker'));
        }
    }, [isPickerFocused, dispatch]);

    const { mainListPosition } = props;
    const isMainListFocused = props.isMainListFocused ?? false;
    const ghostMode = props.ghostMode ?? false;

    const isMouseHighlighted = mainListPosition === props.listItemIndex && isMainListFocused;
    const showHighlight =
        (isPickerFocused && highlightedItemIndex >= 0) ||
        (isMouseHighlighted && !isMainListFocused);

    const isGhostHighlightActive =
        ghostMode &&
        !isPickerFocused &&
        highlightedItemIndex >= 0 &&
        !isMainListFocused &&
        lastFocusedType === 'picker';
    const renderPickerItems = () => {
        if (props.type !== 'picker') {
            return null;
        }

        return (
            <div className="picker-item-container">
                <div className="picker-jump-text">{Locale.getString(LocaleKeys.JUMP_TO)}</div>
                {props.items.map((item) => {
                    const isHighlighted =
                        showHighlight && highlightedItemIndex === item.listItemIndex;
                    const isGhostItem =
                        isGhostHighlightActive && highlightedItemIndex === item.listItemIndex;

                    return (
                        <div
                            key={`picker-item-${item.listItemIndex}`}
                            className="picker-item-layout"
                        >
                            <Mousetrap
                                value={`${props.id}_${item.listItemIndex}_${item.text}_season-picker`}
                                className={`picker-item${isHighlighted ? ' highlighted' : ''}${isGhostItem ? ' ghost' : ''}`}
                            >
                                {item.text}
                            </Mousetrap>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="picker-container">
            <div className="picker-title">{props.title}</div>
            {renderPickerItems()}
        </div>
    );
};

export default SeasonPicker;
